import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import {
  Delete,
  TableChartOutlined,
  CompareArrowsOutlined,
  EditOutlined
} from '@mui/icons-material';
import DynamicField from '../FieldSelector/DynamicField';
import { useAutomationContext } from '../../context/AutomationContext';
import { useAutomationMetadata } from '../../hooks/useAutomationMetadata';
import {
  selectSx,
  labelSx,
  placeholderSx,
  badgeSx,
  mutedBadgeSx,
  statusChipSx,
  tintSx,
  tintedIconButtonSx,
  FONT
} from '../../styles/wizardSx';

const iconLabelSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  ...labelSx
};

function ConditionRow({ condition, conditionIndex, groupLogic, isLastCondition, onUpdate, onRemove }) {
  const { state } = useAutomationContext();
  const { getFieldsForObject, loadOperators, metadata } = useAutomationMetadata();
  const [operators, setOperators] = useState([]);

  const lastLoadedType = useRef(null);
  const isLoadingOperators = useRef(false);

  const fields = getFieldsForObject(state.wizardData.object);
  const selectedField = fields.find(f => f.id === condition.field);

  useEffect(() => {
    const fetchOperators = async () => {
      if (isLoadingOperators.current || lastLoadedType.current === selectedField?.type) {
        return;
      }

      if (selectedField?.type) {
        isLoadingOperators.current = true;
        try {
          const ops = await loadOperators(selectedField.type);
          setOperators(ops || []);
          lastLoadedType.current = selectedField.type;
        } catch (error) {
          console.error('Error loading operators:', error);
          setOperators([]);
        } finally {
          isLoadingOperators.current = false;
        }
      } else {
        setOperators([]);
        lastLoadedType.current = null;
      }
    };

    fetchOperators();
  }, [selectedField?.type]);

  useEffect(() => {
    if (!condition.field) {
      setOperators([]);
      lastLoadedType.current = null;
    }
  }, [condition.field]);

  const isComplete = condition.field && condition.operator && (condition.value !== '' && condition.value !== undefined);

  return (
    <Box
      sx={[
        (theme) => ({
          p: 2,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: isComplete ? 'primary.main' : theme.palette.outline.variant,
          bgcolor: 'background.paper',
          position: 'relative',
          transition: 'border-color 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main'
          }
        }),
        (theme) => (isComplete ? tintSx(theme, 0.03) : {})
      ]}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {/* Condition Number */}
        <Box sx={isComplete ? badgeSx(32) : mutedBadgeSx(32)}>
          {conditionIndex + 1}
        </Box>

        {/* Field Selection */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel sx={iconLabelSx}>
            <TableChartOutlined sx={{ fontSize: 16 }} />
            Field
          </InputLabel>
          <Select
            value={condition.field || ''}
            onChange={(e) => {
              onUpdate({ field: e.target.value, operator: '', value: '' });
            }}
            label="Field"
            sx={selectSx}
            MenuProps={{
              PaperProps: {
                style: { maxHeight: 300 }
              }
            }}
          >
            <MenuItem value="">
              <Typography component="span" sx={placeholderSx}>Select field</Typography>
            </MenuItem>
            {fields.map(field => (
              <MenuItem key={field.id} value={field.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{field.label}</Typography>
                  {field.metadata?.is_custom && (
                    <Chip
                      label="Custom"
                      size="small"
                      sx={[{ height: '20px' }, statusChipSx('info')]}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Operator Selection */}
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel sx={iconLabelSx}>
            <CompareArrowsOutlined sx={{ fontSize: 16 }} />
            Operator
          </InputLabel>
          <Select
            value={condition.operator || ''}
            onChange={(e) => onUpdate({ operator: e.target.value })}
            label="Operator"
            disabled={!condition.field || operators.length === 0}
            sx={selectSx}
          >
            <MenuItem value="">
              <Typography component="span" sx={placeholderSx}>Select operator</Typography>
            </MenuItem>
            {operators.map(op => (
              <MenuItem key={op.id} value={op.id}>
                {op.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Value Field */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          {selectedField && condition.operator ? (
            <DynamicField
              field={selectedField}
              value={condition.value || ''}
              onChange={(value) => onUpdate({ value })}
              operator={condition.operator}
              strictPicklist
            />
          ) : (
            <Box sx={{
              height: '45px',
              borderRadius: '8px',
              border: '1px dashed',
              borderColor: 'outline.variant',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'surface.main',
              px: 2,
              textAlign: 'center',
              overflow: 'hidden'
            }}>
              <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
                {!condition.field ? 'Select field first' : 'Select operator first'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Remove Button */}
        <Tooltip title="Remove condition">
          <IconButton
            onClick={onRemove}
            aria-label="Remove condition"
            sx={tintedIconButtonSx('error')}
          >
            <Delete sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Completion Indicator */}
      {isComplete && (
        <Chip
          label="Complete"
          size="small"
          sx={[
            {
              position: 'absolute',
              top: -10,
              right: 16,
              height: '20px'
            },
            statusChipSx('success')
          ]}
        />
      )}
    </Box>
  );
}

export default ConditionRow;
